import React, { useState, useEffect } from 'react';
import { managerAPI } from '../config/api';
import APP_COLOURS from '../config/colors';
import QRCode from 'react-qr-code';
import moment from 'moment';
import { useTheme } from '../theme';

const DeviceManagement = ({ onClose, staffList = [] }) => {
  const { scheme } = useTheme();
  const qrFg = scheme === 'dark' ? '#FFFFFF' : '#000000';
  const qrBg = scheme === 'dark' ? '#000000' : '#FFFFFF';
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mallFilter, setMallFilter] = useState('all');
  const [retailerFilter, setRetailerFilter] = useState('all');

  const staffMap = {};
  staffList.forEach(s => {
    if (s.id) staffMap[s.id] = s;
  });

  const resolveUserId = (userEntry) => {
    if (typeof userEntry === 'string') return userEntry;
    if (userEntry && typeof userEntry === 'object') return userEntry.id || userEntry.uid || '';
    return '';
  };

  const getDeviceStaff = (device) => {
    if (!device.users || !Array.isArray(device.users)) return [];
    const resolvedIds = device.users.map(resolveUserId).filter(Boolean);
    return resolvedIds
      .map(uid => staffMap[uid] ? { ...staffMap[uid], _matched: true } : { id: uid, first_name: uid, last_name: '', _matched: false })
      .filter(s => s._matched);
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      setLoading(true);
      const data = await managerAPI.getDevices();
      console.log('Devices data received:', data);
      
      // Check if data is an array
      if (Array.isArray(data)) {
        // Sanitize device data to ensure no objects are rendered directly
        const sanitizedDevices = data.map(device => ({
          ...device,
          // Ensure users is always an array
          users: Array.isArray(device.users) ? device.users : [],
          // Extract title from mall/retailer objects if they exist
          mall: device.mall?.title || device.mall || '',
          retailer: device.retailer?.title || device.retailer || '',
          // Ensure strings for display fields
          id: device.id || '',
          active: device.active || false,
          date_created: device.date_created || null,
          passcode: device.passcode || '',
          device_id: device.device_id || ''
        }));
        
        console.log('Sanitized devices:', sanitizedDevices);
        setDevices(sanitizedDevices);
        setError('');
      } else {
        console.error('Invalid devices data format:', data);
        setError('Invalid data format received from server');
        setDevices([]);
      }
    } catch (err) {
      console.error('Error fetching devices:', err);
      // Better error message handling
      let errorMessage = 'Failed to fetch devices';
      
      if (typeof err === 'string') {
        errorMessage = err;
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.error) {
        errorMessage = err.error;
      }
      
      setError(errorMessage);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceClick = (device) => {
    setSelectedDevice(device);
    setShowQRModal(true);
  };

  const downloadQRCode = () => {
    const svg = document.getElementById('qr-code-svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `TRAQ_Device_${selectedDevice?.id}.png`;
        link.click();
        URL.revokeObjectURL(url);
      });
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  // Get unique malls and retailers for filter dropdowns
  const uniqueMalls = [...new Set(devices.map(d => d.mall).filter(Boolean))].sort();
  const uniqueRetailers = [...new Set(devices.map(d => d.retailer).filter(Boolean))].sort();

  const filteredDevices = devices.filter(device => {
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const mall = device.mall?.toLowerCase() || '';
      const retailer = device.retailer?.toLowerCase() || '';
      const id = device.id?.toString().toLowerCase() || '';
      
      const matchesSearch = mall.includes(query) || retailer.includes(query) || id.includes(query);
      if (!matchesSearch) return false;
    }
    
    // Mall filter
    if (mallFilter !== 'all' && device.mall !== mallFilter) {
      return false;
    }
    
    // Retailer filter
    if (retailerFilter !== 'all' && device.retailer !== retailerFilter) {
      return false;
    }
    
    return true;
  });

  if (loading) {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      >
        <div className="text-center">
          <div 
            className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
            style={{ borderColor: APP_COLOURS.BLUE }}
          ></div>
          <p style={{ color: '#FFF' }}>Loading devices...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
    >
      {/* Main Container */}
      <div className="min-h-screen px-4 py-8">
        <div 
          className="max-w-6xl mx-auto rounded-2xl shadow-2xl"
          style={{ backgroundColor: APP_COLOURS.BG }}
        >
          {/* Header */}
          <div 
            className="p-6 border-b flex items-center justify-between"
            style={{ borderColor: APP_COLOURS.BG4 }}
          >
            <div>
              <h2 className="text-3xl font-bold flex items-center" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                <i className="fas fa-tablet-alt mr-3" style={{ color: APP_COLOURS.BLUE }}></i>
                Device Management
              </h2>
              <p className="text-sm mt-1" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                View device information and QR codes
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-3 rounded-lg transition-all duration-200"
              style={{ 
                backgroundColor: APP_COLOURS.BG3,
                color: APP_COLOURS.TEXTCOLOR 
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = APP_COLOURS.BG4;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = APP_COLOURS.BG3;
              }}
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div 
              className="mx-6 mt-6 rounded-xl p-4"
              style={{ 
                backgroundColor: 'rgba(255, 95, 95, 0.1)', 
                border: `1px solid ${APP_COLOURS.ERROR}` 
              }}
            >
              <div className="flex items-center">
                <i className="fas fa-exclamation-circle mr-3" style={{ color: APP_COLOURS.ERROR }}></i>
                <p style={{ color: APP_COLOURS.ERROR }}>{error}</p>
              </div>
            </div>
          )}

          {/* Filters Section */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Search Bar */}
              <div className="relative">
                <i 
                  className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2"
                  style={{ color: APP_COLOURS.PLACEHOLDER }}
                ></i>
                <input
                  type="text"
                  placeholder="Search by ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-lg outline-none transition-all duration-200"
                  style={{
                    backgroundColor: APP_COLOURS.BG3,
                    border: `1px solid ${APP_COLOURS.BG4}`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                />
              </div>

              {/* Mall Filter */}
              <div className="relative">
                <select
                  value={mallFilter}
                  onChange={(e) => setMallFilter(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg outline-none transition-all duration-200 appearance-none cursor-pointer"
                  style={{
                    backgroundColor: APP_COLOURS.BG3,
                    border: `1px solid ${APP_COLOURS.BG4}`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                >
                  <option value="all">All Malls</option>
                  {uniqueMalls.map(mall => (
                    <option key={mall} value={mall}>{mall}</option>
                  ))}
                </select>
                <i 
                  className="fas fa-chevron-down absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ color: APP_COLOURS.PLACEHOLDER }}
                ></i>
              </div>

              {/* Retailer Filter */}
              <div className="relative">
                <select
                  value={retailerFilter}
                  onChange={(e) => setRetailerFilter(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg outline-none transition-all duration-200 appearance-none cursor-pointer"
                  style={{
                    backgroundColor: APP_COLOURS.BG3,
                    border: `1px solid ${APP_COLOURS.BG4}`,
                    color: APP_COLOURS.TEXTCOLOR,
                  }}
                >
                  <option value="all">All Retailers</option>
                  {uniqueRetailers.map(retailer => (
                    <option key={retailer} value={retailer}>{retailer}</option>
                  ))}
                </select>
                <i 
                  className="fas fa-chevron-down absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{ color: APP_COLOURS.PLACEHOLDER }}
                ></i>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                {filteredDevices.length} device{filteredDevices.length !== 1 ? 's' : ''} found
              </div>
              
              {/* Clear Filters Button */}
              {(mallFilter !== 'all' || retailerFilter !== 'all' || searchQuery) && (
                <button
                  onClick={() => {
                    setMallFilter('all');
                    setRetailerFilter('all');
                    setSearchQuery('');
                  }}
                  className="text-sm px-3 py-1 rounded-lg transition-all duration-200"
                  style={{ 
                    color: APP_COLOURS.BLUE,
                    backgroundColor: `${APP_COLOURS.BLUE}20`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  <i className="fas fa-times mr-1"></i>
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Devices Table */}
          <div className="px-6 pb-6">
            {filteredDevices.length === 0 ? (
              <div className="text-center py-12">
                <i 
                  className="fas fa-tablet-alt text-6xl mb-4"
                  style={{ color: APP_COLOURS.PLACEHOLDER, opacity: 0.3 }}
                ></i>
                <p className="text-lg" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                  No devices found
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg" style={{ border: `1px solid ${APP_COLOURS.BG4}` }}>
                <table className="w-full">
                  <thead>
                    <tr style={{ backgroundColor: APP_COLOURS.BG3 }}>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                        style={{ color: APP_COLOURS.PLACEHOLDER }}
                      >
                        Device ID
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                        style={{ color: APP_COLOURS.PLACEHOLDER }}
                      >
                        Mall
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                        style={{ color: APP_COLOURS.PLACEHOLDER }}
                      >
                        Retailer
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                        style={{ color: APP_COLOURS.PLACEHOLDER }}
                      >
                        Status
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                        style={{ color: APP_COLOURS.PLACEHOLDER }}
                      >
                        Allocated Staff
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                        style={{ color: APP_COLOURS.PLACEHOLDER }}
                      >
                        Created
                      </th>
                      <th 
                        className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider"
                        style={{ color: APP_COLOURS.PLACEHOLDER }}
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDevices.map((device, index) => (
                      <tr
                        key={device.id}
                        className="cursor-pointer transition-all duration-200"
                        style={{
                          backgroundColor: index % 2 === 0 ? APP_COLOURS.BG : APP_COLOURS.BG2,
                          borderTop: `1px solid ${APP_COLOURS.BG4}`,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = APP_COLOURS.BG3;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = index % 2 === 0 ? APP_COLOURS.BG : APP_COLOURS.BG2;
                        }}
                        onClick={() => handleDeviceClick(device)}
                      >
                        {/* Device ID */}
                        <td className="px-4 py-4">
                          <div className="flex items-center">
                            <div 
                              className="h-8 w-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0"
                              style={{ background: `linear-gradient(to right, ${APP_COLOURS.BLUE}, #9333ea)` }}
                            >
                              <i className="fas fa-tablet-alt text-white text-sm"></i>
                            </div>
                            <span className="font-semibold text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                              {device.id}
                            </span>
                          </div>
                        </td>

                        {/* Mall */}
                        <td className="px-4 py-4">
                          <span className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                            {device.mall || '-'}
                          </span>
                        </td>

                        {/* Retailer */}
                        <td className="px-4 py-4">
                          <span className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                            {device.retailer || '-'}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium inline-block ${
                              device.active ? 'bg-green-500' : 'bg-red-500'
                            }`}
                            style={{ color: '#FFF' }}
                          >
                            {device.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>

                        {/* Allocated Staff */}
                        <td className="px-4 py-4">
                          {(() => {
                            const matched = getDeviceStaff(device);
                            const totalUsers = (device.users || []).length;
                            if (matched.length === 0) {
                              return (
                                <span className="text-xs" style={{ color: APP_COLOURS.PLACEHOLDER, opacity: 0.5 }}>
                                  {totalUsers > 0 ? `${totalUsers} user${totalUsers !== 1 ? 's' : ''} (none yours)` : 'No staff'}
                                </span>
                              );
                            }
                            return (
                              <div className="flex flex-col gap-1.5">
                                {matched.map((s) => (
                                  <div key={s.id} className="flex items-center gap-1.5">
                                    <div
                                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                                      style={{
                                        background: `linear-gradient(135deg, ${APP_COLOURS.BLUE}, #9333ea)`,
                                        fontSize: '7px',
                                        color: '#fff',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {(s.first_name?.[0] || '') + (s.last_name?.[0] || '')}
                                    </div>
                                    <span className="text-xs truncate" style={{ color: APP_COLOURS.TEXTCOLOR, maxWidth: '160px' }}>
                                      {s.first_name} {s.last_name}
                                    </span>
                                  </div>
                                ))}
                                {totalUsers > matched.length && (
                                  <span className="text-[10px]" style={{ color: APP_COLOURS.PLACEHOLDER, opacity: 0.5 }}>
                                    +{totalUsers - matched.length} other{totalUsers - matched.length !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>

                        {/* Created */}
                        <td className="px-4 py-4">
                          <span className="text-sm" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                            {device.date_created ? moment(device.date_created).format('MMM DD, YYYY') : '-'}
                          </span>
                        </td>

                        {/* Action */}
                        <td className="px-4 py-4 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeviceClick(device);
                            }}
                            className="px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 inline-flex items-center"
                            style={{
                              backgroundColor: APP_COLOURS.BLUE,
                              color: '#FFF',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = '0.9';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.opacity = '1';
                            }}
                          >
                            <i className="fas fa-qrcode mr-2"></i>
                            View QR
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRModal && selectedDevice && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
          onClick={() => setShowQRModal(false)}
        >
          <div
            className="max-w-lg w-full mx-4 rounded-2xl overflow-hidden"
            style={{ backgroundColor: APP_COLOURS.BG2 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div 
              className="p-6 border-b flex items-center justify-between"
              style={{ borderColor: APP_COLOURS.BG4 }}
            >
              <h3 className="text-xl font-bold" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                Device QR Code
              </h3>
              <button
                onClick={() => setShowQRModal(false)}
                className="p-2 rounded-lg transition-all duration-200"
                style={{ 
                  backgroundColor: APP_COLOURS.BG3,
                  color: APP_COLOURS.TEXTCOLOR 
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-8 text-center">
              {/* App Info */}
              <div className="mb-6">
                <h4 
                  className="text-2xl font-bold mb-2"
                  style={{ color: APP_COLOURS.TEXTCOLOR }}
                >
                  TRAQ Staff App
                </h4>
                <p 
                  className="text-sm"
                  style={{ color: APP_COLOURS.PLACEHOLDER }}
                >
                  Scan to download and register device
                </p>
              </div>

              {/* QR Code */}
              <div className="flex justify-center mb-6">
                <div 
                  className="p-6 rounded-2xl inline-block"
                  style={{ backgroundColor: qrBg }}
                >
                  <QRCode
                    id="qr-code-svg"
                    size={280}
                    value="https://traq.app"
                    bgColor={qrBg}
                    fgColor={qrFg}
                  />
                </div>
              </div>

              <p 
                className="text-lg font-bold mb-6"
                style={{ color: APP_COLOURS.ERROR }}
              >
                SCAN ME
              </p>

              {/* Device Details */}
              <div 
                className="mb-6 p-4 rounded-lg text-left"
                style={{ backgroundColor: APP_COLOURS.BG3 }}
              >
                <p className="text-xs mb-2" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                  Device Information
                </p>
                <div className="space-y-1">
                  <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                    <span className="font-semibold">ID:</span> {selectedDevice.id}
                  </p>
                  {selectedDevice.mall && (
                    <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      <span className="font-semibold">Mall:</span> {selectedDevice.mall}
                    </p>
                  )}
                  {selectedDevice.retailer && (
                    <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      <span className="font-semibold">Retailer:</span> {selectedDevice.retailer}
                    </p>
                  )}
                  {selectedDevice.passcode && (
                    <p className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>
                      <span className="font-semibold">Passcode:</span> {selectedDevice.passcode}
                    </p>
                  )}
                </div>
              </div>

              {/* Download Button */}
              <button
                onClick={downloadQRCode}
                className="w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center"
                style={{
                  backgroundColor: APP_COLOURS.BLUE,
                  color: '#FFF',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                <i className="fas fa-download mr-2"></i>
                Download QR Code
              </button>

              {/* App Store Links */}
              <div className="mt-6 pt-6 border-t" style={{ borderColor: APP_COLOURS.BG4 }}>
                <p className="text-xs mb-3" style={{ color: APP_COLOURS.PLACEHOLDER }}>
                  Available on
                </p>
                <div className="flex justify-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <i className="fab fa-apple text-2xl" style={{ color: APP_COLOURS.TEXTCOLOR }}></i>
                    <span className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>iOS</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <i className="fab fa-android text-2xl" style={{ color: APP_COLOURS.TEXTCOLOR }}></i>
                    <span className="text-sm" style={{ color: APP_COLOURS.TEXTCOLOR }}>Android</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceManagement;

